import React, { useState, useEffect, useRef } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import api from '@helpers/api';
import { useCommunity } from "contexts/community";
import Parser from '@helpers/parser';
import { AttachMoneyRounded, PixRounded, SellRounded, UndoRounded } from '@mui/icons-material';
import { ReactComponent as MastercardIcon } from '@assets/custom-icons/mastercard.svg';
import { ReactComponent as EloIcon } from '@assets/custom-icons/elo.svg';
import { ReactComponent as VisaIcon } from '@assets/custom-icons/visa.svg';
import Popper from '@components/Popper';
import Table from '@components/Table';
import StatusChip from '@components/SmallComponents/StatusChip';

import { Text } from "@fragments";

export function ChargesTable({ userId, displayTitle = false, variant = 'transparent' }) {
    const [charges, setCharges] = useState([]);
    const [loading, setLoading] = useState(true);
    const popperRef = useRef();

    const { community, loading: communityLoading } = useCommunity();

    useEffect(() => {
        if (userId && !communityLoading) {
            fetchCharges();
        }
    }, [userId, communityLoading]);

    const fetchCharges = async () => {
        try {
            setLoading(true);
            let { data } = await api.get(`charge?member=${userId}`);
            console.log('Charges data:', data);
            if (data.length > 0) {
                data = data.map(charge => {
                    charge.community = community.get(charge.community);
                    return charge;
                });
            }
            console.log('Processed charges:', data);
            setCharges([...(data || [])]);
        } catch (error) {
            console.error('Error fetching charges:', error);
            setCharges([]);
        } finally {
            setLoading(false);
        }
    };

    const getMethodIcon = (method, cardBrand) => {
        if (method === 'credit') {
            const brand = cardBrand?.toLowerCase() || 'visa';
            return (
                <>
                    {{
                        'visa': <VisaIcon />,
                        'elo': <EloIcon />,
                        'mastercard': <MastercardIcon />
                    }[brand]}
                </>
            );
        } else if (method === 'pix') {
            return <PixRounded />;
        }
        return <AttachMoneyRounded />;
    };

    const formatAmount = (amount, installments) => {
        if (!amount) return 'R$0,00';

        const totalAmount = amount; // Convert from cents
        const formattedAmount = Parser.price.brl(totalAmount);

        if (installments && installments > 1) {
            const installmentAmount = totalAmount / installments;
            const formattedInstallment = Parser.price.brl(installmentAmount);
            return `R$${formattedAmount} (${installments}x R$${formattedInstallment})`;
        }

        return `R$${formattedAmount}`;
    };

    const getCardNumber = (charge) => {
        if (charge.method === 'credit' && charge.card?.last_four) {
            return `• ${charge.card.last_four}`;
        }
        return '';
    };

    const rowsConfig = [
        {
            title: 'Produto',
            className: 'pl-1',
            customRender: (data) => (
                <>
                    <img
                        src={data.community?.banner || ''}
                        className="community-row-banner"
                    />
                    <span className="community-name">
                        {data.community?.name || 'N/A'}
                    </span>
                </>
            )
        },
        {
            title: 'Status',
            align: 'center',
            customRender: (data) => {
                const StatusComponent = {
                    'paid': StatusChip.Success,
                    'pending': StatusChip.Alert,
                    'processing': StatusChip.Alert,
                    'canceled': StatusChip.Error,
                    'refunded': StatusChip.Error,
                    'default': StatusChip.Default
                }[data.status || 'default'];

                return <StatusComponent text={Parser.payment.chargeStatus(data.status)} />;
            }
        },
        {
            title: 'Valor bruto',
            render: (data) => formatAmount(data.amount, data.installments)
        },
        {
            title: 'Método',
            align: 'center',
            customRender: (data) => (
                <>
                    {getMethodIcon(data.method, data.card?.brand)}
                    {data.method === 'credit' && (
                        <label className="card-number">
                            {getCardNumber(data)}
                        </label>
                    )}
                </>
            )
        },
        {
            title: 'Data',
            render: (data) => Parser.date.readable(data.created)
        },
        {
            className: 'actions',
            customRender: (data) => (
                <button
                    className={`action-button ${data.status?.toLowerCase() !== 'paid' ? 'disabled' : ''}`}
                    onClick={(event) => handleOpenPopper(event, data)}
                    disabled={data.status?.toLowerCase() !== 'paid'}
                >
                    <MoreVertIcon />
                </button>
            )
        }
    ];


    const handleOpenPopper = (event, charge) => {
        const options = [];

        if (charge.status?.toLowerCase() === 'paid') {
            options.push({
                id: 'refund',
                label: 'Realizar estorno',
                icon: <UndoRounded fontSize="small" />,
                variant: 'warning',
                onClick: () => {
                    window.openConfirm({
                        title: 'Realizar estorno',
                        subtitle: 'Tem certeza que deseja realizar o estorno desta cobrança?',
                        buttons: [
                            {
                                text: "Sim, realizar estorno",
                                onClick: async () => {
                                    try {
                                        const { data } = await api.delete('charge/refund?id=' + charge.id);

                                        fetchCharges()
                                    } catch (error) {
                                        console.error('Erro ao realizar estorno:', error);
                                    }
                                },
                                variant: "contained",
                                className: "",
                                color: "error",
                            },
                            {
                                text: "Manter cobrança",
                                variant: "text",
                            },
                        ],
                    });
                }
            });
        }

        if (options.length === 0) return;

        popperRef.current.open(options, 'bottom', event.currentTarget);
    };

    return (
        <>
            {
                displayTitle && <Text.h3 value="Histórico de Cobranças" className='mb-2 mt-5' left={<SellRounded />} />
            }
            <Table
                data={charges}
                loading={loading}
                loadingRows={4}
                emptyMessage="Nenhuma cobrança encontrada"
                layout="charges-layout"
                rowsConfig={rowsConfig}
                variant={variant}
            />
            <Popper controlRef={popperRef} />
        </>
    );
}
